import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../../context/AuthContext';
import { ChevronLeft, Sparkles, Plus, Minus, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const PointsAdjuster = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const [delta, setDelta] = useState(10);
  const [reason, setReason] = useState('Forgot to mark complete');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // Try admin endpoint first; if barber, fall back to barber's customer list
        let list = [];
        try {
          const res = await api.get('/api/admin/users');
          list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        } catch (e) {
          // Barber path — fetch via barber appointments to extract their customers
          const res = await api.get('/api/barber/appointments');
          const appts = Array.isArray(res.data) ? res.data : [];
          const map = new Map();
          appts.forEach((a) => {
            if (a.customer_id && !map.has(a.customer_id)) {
              map.set(a.customer_id, {
                id: a.customer_id,
                name: a.customer_name,
                phone: a.customer_phone,
                email: a.customer_email,
                role: 'customer',
                loyalty_points: 0,
              });
            }
          });
          list = Array.from(map.values());
        }
        const customers = list.filter((u) => (u.role || 'customer') === 'customer');
        setUsers(customers);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load customers');
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [isAdmin]);

  const filtered = users
    .filter((u) => {
      const f = filter.trim().toLowerCase();
      if (!f) return true;
      return (
        (u.name || '').toLowerCase().includes(f) ||
        (u.email || '').toLowerCase().includes(f) ||
        (u.phone || '').toLowerCase().includes(f)
      );
    })
    .slice(0, 30);

  const handleAdjust = async () => {
    if (!selected) {
      toast.error('Pick a customer first');
      return;
    }
    const points = parseInt(delta, 10);
    if (Number.isNaN(points) || points === 0) {
      toast.error('Enter a non-zero amount');
      return;
    }
    if (!reason.trim()) {
      toast.error('Add a reason (visible to the customer)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/api/rewards/admin/adjust', {
        target_user_id: selected.id,
        points,
        reason: reason.trim(),
      });
      setLastResult({
        customer: selected.name || selected.email,
        delta: res.data.adjustment,
        new_balance: res.data.new_balance,
      });
      toast.success(
        points > 0
          ? `Added ${points} pts to ${selected.name || selected.email}`
          : `Removed ${Math.abs(points)} pts from ${selected.name || selected.email}`
      );
      setSelected(null);
      setFilter('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Adjustment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const quickValues = [5, 10, 15, 20, 25, 50];

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-8" data-testid="points-adjuster-page">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-white"
          onClick={() => navigate(isAdmin ? '/admin' : '/barber-dashboard')}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-lg bg-lime-500/10 border border-lime-500/30">
            <Sparkles className="w-7 h-7 text-lime-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Adjust Loyalty Points</h1>
            <p className="text-zinc-400 text-sm mt-1">
              Fix missed completions, give bonuses, or correct mistakes.
            </p>
          </div>
        </div>

        {lastResult && (
          <Card className="bg-lime-500/10 border-lime-500/40 mb-6" data-testid="adjust-success-card">
            <CardContent className="p-4">
              <p className="text-lime-300 font-semibold">
                {lastResult.delta > 0 ? '✓ Added' : '✓ Removed'} {Math.abs(lastResult.delta)} pts —{' '}
                {lastResult.customer}
              </p>
              <p className="text-zinc-400 text-sm">
                New balance: <span className="text-white font-semibold">{lastResult.new_balance} pts</span>
              </p>
            </CardContent>
          </Card>
        )}

        {/* 1. Pick customer */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Search className="w-5 h-5 text-zinc-400" /> 1. Find the customer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Search by name, email, or phone…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-zinc-950 border-zinc-700 text-white"
              data-testid="customer-search"
            />
            {loadingUsers ? (
              <p className="text-center text-zinc-500 py-6">Loading…</p>
            ) : (
              <div className="mt-4 max-h-72 overflow-y-auto border border-zinc-800 rounded-lg divide-y divide-zinc-800">
                {filtered.length === 0 ? (
                  <p className="p-6 text-center text-zinc-500">No matching customers.</p>
                ) : filtered.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => setSelected(u)}
                    className={`w-full text-left px-4 py-3 hover:bg-zinc-800 transition flex items-center justify-between ${
                      selected?.id === u.id ? 'bg-lime-500/10 ring-1 ring-lime-500/40' : ''
                    }`}
                    data-testid={`customer-row-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{u.name || '(no name)'}</p>
                      <p className="text-xs text-zinc-400 truncate">{u.email}</p>
                    </div>
                    <span className="ml-3 text-xs text-lime-400 font-semibold whitespace-nowrap">
                      {(u.loyalty_points || 0)} pts
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2. Adjust */}
        <Card className={`bg-zinc-900 border-zinc-800 ${!selected ? 'opacity-50' : ''}`}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Plus className="w-5 h-5 text-lime-400" /> 2. Adjust points
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selected ? (
              <>
                <p className="text-sm text-zinc-400 mb-4">
                  Adjusting: <span className="text-white font-semibold">{selected.name || selected.email}</span>{' '}
                  · Current: <span className="text-lime-400 font-semibold">{selected.loyalty_points || 0} pts</span>
                </p>

                <Label className="text-zinc-300">Points to add (use negative to remove)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    type="button"
                    onClick={() => setDelta((d) => -Math.abs(parseInt(d, 10) || 0))}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                    data-testid="make-negative-btn"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={delta}
                    onChange={(e) => setDelta(e.target.value)}
                    className="bg-zinc-950 border-zinc-700 text-white text-center"
                    data-testid="delta-input"
                  />
                  <Button
                    type="button"
                    onClick={() => setDelta((d) => Math.abs(parseInt(d, 10) || 0))}
                    variant="outline"
                    className="border-zinc-700 hover:bg-zinc-800"
                    data-testid="make-positive-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {quickValues.map((v) => (
                    <button
                      key={v}
                      onClick={() => setDelta(v)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                        parseInt(delta, 10) === v
                          ? 'bg-lime-400 text-black'
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      }`}
                    >
                      +{v}
                    </button>
                  ))}
                </div>

                <Label className="text-zinc-300 mt-5 block">Reason (shown to the customer)</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder='e.g. "Forgot to mark complete — Tuesday haircut"'
                  className="bg-zinc-950 border-zinc-700 text-white mt-1"
                  data-testid="reason-input"
                  maxLength={120}
                />

                <div className="mt-4 p-3 rounded-lg bg-zinc-950/70 border border-zinc-800 text-sm">
                  <p className="text-zinc-500">Preview:</p>
                  <p className="text-zinc-200 mt-1">
                    <span className={parseInt(delta, 10) >= 0 ? 'text-lime-400' : 'text-red-400'}>
                      {parseInt(delta, 10) >= 0 ? `+${parseInt(delta, 10) || 0}` : parseInt(delta, 10)}
                    </span>{' '}
                    pts → New balance:{' '}
                    <span className="text-white font-semibold">
                      {Math.max(0, (selected.loyalty_points || 0) + (parseInt(delta, 10) || 0))} pts
                    </span>
                  </p>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => { setSelected(null); setLastResult(null); }}
                    disabled={submitting}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdjust}
                    disabled={submitting || !delta || parseInt(delta, 10) === 0}
                    className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-semibold"
                    data-testid="adjust-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {submitting ? 'Adjusting…' : 'Apply adjustment'}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Select a customer above to adjust their points.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PointsAdjuster;
